import {NullableStation, Station} from "../stations/station.service.interface";
import {IFlightSearchService,} from "./flight-search.service.interface";
import {IServiceFactory} from "../service-factory.interface";
import {ServiceBase} from "../service-base";
import {FlightSearchControllerModel} from "./flight-search-controller/flight-search-controller.model";
import {RoundTripLowFareReaderFactory} from "../low-fare/round-trip-low-fare-reader-factory";
import {IRoundTripLowFareReader} from "../low-fare/low-fare-readers/low-fare-reader.interface";
import {BookingEngineUrlBuilder} from "./booking-engine-url-builder";
import {WellKnownQueryParams} from "../window/window.service.interface";
import {buildFullUrl} from "../http/http-helpers";
import {PsoUserOptionsEnum} from "./flight-search-controller/pso.enums";
import {RawLocalStorageKeys} from "../storage/raw-local-storage/raw-local-storage-keys";
import {RawSessionStorageKeys} from "../storage/raw-session-storage/raw-session-storage-keys";


export class FlightSearchService extends ServiceBase implements IFlightSearchService {
    constructor(services: IServiceFactory) {
        super(services);

        this.searchController = new FlightSearchControllerModel(this.services, RawSessionStorageKeys.flightSearchSearchControllerStorageKey);
        this._lowFareReaderFactory = new RoundTripLowFareReaderFactory(
            this.services,
            this.searchController,
            true,
            () => this.services.currency.current);


    }

    readonly searchController: FlightSearchControllerModel;

    private readonly _lowFareReaderFactory: RoundTripLowFareReaderFactory;
    getLowFareReader(): IRoundTripLowFareReader {
        return this._lowFareReaderFactory.lowFareReader;

    }

    private _getRecentlySearch(key: RawLocalStorageKeys): Station[] {
        let result: Station[] = [];
        const savedStations = this.services.rawLocalStorage.getJson<string[]>(key) || [];

        for(let stationCode of savedStations) {
            const s = this.services.stations.tryGetStation(stationCode);
            if(s) {
                result.push(s);
            }
        }

        return result;
    }

    getRecentlySearchedOrigins(): Station[] {
        return this._getRecentlySearch(RawLocalStorageKeys.flightSearchRecentlySearchedOriginStations);
    }
        
    getRecentlySearchedDestinations(): Station[] {
        return this._getRecentlySearch(RawLocalStorageKeys.flightSearchRecentlySearchedDestinationStations);
    }        
        
    private _saveRecentlySearch(key: RawLocalStorageKeys, station: NullableStation) {
        if(!station) {
            return;
        }
        let recentlySearch = this.services.rawLocalStorage.getJson<string[]>(key) || [];
        const stationIndex = recentlySearch.indexOf(station.stationCode);

        if (stationIndex >= 0) {
            recentlySearch.splice(stationIndex, 1);
        }

        recentlySearch.splice(0, 0, station.stationCode); 

        recentlySearch = recentlySearch.slice(0, 3);

        this.services.rawLocalStorage.setJson(key, recentlySearch);
    }

    private _getBookingEngineSearchUrl(): string {
        const bookingEngineUrl = this.services.configuration.data.bookingEngineUrl?.toLowerCase();
        if(!bookingEngineUrl) {
            throw new Error('bookingEngineUrl was not configured');
        }

        if(bookingEngineUrl.endsWith('deeplink/search')) {
            return bookingEngineUrl;
        }

        return buildFullUrl(bookingEngineUrl, 'deeplink/search');
    }
    async startBooking(): Promise<void> {

        const fieldsWithError = this.searchController.activateErrorsValidation();
        if(fieldsWithError.length > 0) {
            return;
        }


        if(this.searchController.passengers.countDogs() > 0 && this.searchController.psoUserOption !== PsoUserOptionsEnum.None) {
            await this.services.dialog.showSimpleMessage({
                title: this.services.language.translate('Attention'),
                message: this.services.language.translate('You cannot book Territorial Continuity online when you travel with a dog. However, you can request this when our staff will contact you after you finish this booking.'),
                buttonText: this.services.language.translate('OK'),
            });
            this.searchController.psoUserOption = PsoUserOptionsEnum.None;
        }

        this._saveRecentlySearch(RawLocalStorageKeys.flightSearchRecentlySearchedOriginStations, this.searchController.origin);
        this._saveRecentlySearch(RawLocalStorageKeys.flightSearchRecentlySearchedDestinationStations, this.searchController.destination);

        const bookingEngineUrl = this._getBookingEngineSearchUrl();

        const urlBuilder = new BookingEngineUrlBuilder();

        urlBuilder.isRoundTrip = (!this.searchController.isOneWay && Boolean(this.searchController.returnDate)) //!this.searchController.isOneWay;
        urlBuilder.origin1 = this.searchController.origin?.stationMacCode ?? "";
        urlBuilder.destination1 = this.searchController.destination?.stationMacCode ?? "";
        urlBuilder.departureDate1 = this.services.time.formatYYYY_MM_DD(this.searchController.departureDate);


        urlBuilder.psoUserOption = this.searchController.fields.psoUserOption.value ?? PsoUserOptionsEnum.None;
        if(urlBuilder.isRoundTrip) {
            urlBuilder.returnDate1 = this.services.time.formatYYYY_MM_DD(this.searchController.returnDate);
        }

        urlBuilder.passengersTypes = this.searchController.passengers;

        urlBuilder.culture = this.services.language.currentLanguage;
        urlBuilder.bookingCurrency = this.services.currency.current;


        urlBuilder.agent = (this.services.window.getUrlParam(WellKnownQueryParams.Agent) ?? "");


        const url = urlBuilder.buildUrl(bookingEngineUrl);
        this.services.window.redirect(url.href);
    }

    switchStations() {
        let tempStation = this.searchController.origin;
        let departureDate = this.searchController.departureDate;
        let returnDate = this.searchController.returnDate;
        this.searchController.origin = this.searchController.destination;
        this.searchController.destination = tempStation;
        this.searchController.departureDate = departureDate;
        this.searchController.returnDate = returnDate;
    }

}
